import { gql } from '@apollo/client';

export const GET_IMPLEMENTATION_DETAILS = gql`
  query GetImplementationDetails($implementationId: UUID!) {
    getImplementationDetails(implementationId: $implementationId) {
      id
      slug
      name
      ownerOrganization {
        id
        name
        slug
      }
      borrowerOrganization {
        id
        name
        slug
        type
        industry
      }
      loanProducts {
        id
        slug
        name
      }
      creditFacilities(includeInactive: false) {
        id
        slug
        name
        borrower {
          id
          name
          slug
        }
      }
      borrowerPipelineDefinitionId
    }
  }
`;

export const GET_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENTS = gql`
  query getBorrowingBasePipelineArtifactDocuments(
    $pipelineDefinitionId: UUID!
  ) {
    borrowingBasePipelineArtifactDocuments(
      pipelineDefinitionId: $pipelineDefinitionId
    ) {
      id
      version
      isWip
    }
  }
`;

export const GET_TAPE_BUILDER_STATUS = gql`
  query getBorrowingBasePipelineStatus($pipelineDefinitionId: UUID!) {
    implementation: implementationByPipelineDefinitionId(
      pipelineDefinitionId: $pipelineDefinitionId
    ) {
      borrowerOrganization {
        id
        slug
      }
      creditFacilities {
        id
        slug
      }
      loanProducts {
        id
        slug
      }
    }
    borrowingBasePipelineStatus(pipelineDefinitionId: $pipelineDefinitionId) {
      latestArtifactDocumentId
      wipNeedsCommit
      creditAgreementsNeedLink
      latestArtifactDocumentVersion
      artifactDocumentWipId
    }
  }
`;

export const GET_LOAN_TAPE_METADATA = gql`
  query getLoanTapeMetadata(
    $creditFacilityId: UUID!
    $loanProductId: UUID!
    $reportingDate: FullDateStr!
    $fileVersion: Int!
  ) {
    loanTapeMetadata(
      creditFacilityId: $creditFacilityId
      loanProductId: $loanProductId
      reportingDate: $reportingDate
      fileVersion: $fileVersion
    ) {
      commitmentAmount
      principalBalance
      drawRequestAmountForCalendarMonth
      lastApprovedOrSubmittedFundingRequestDate
      reportingDate
      fileVersion
      portfolioCovenants
      customerProvidedValues
    }
  }
`;

export const GET_PDF_ANALYSIS = gql`
  query getPdfAnalysis($input: PdfAnalysisParams!) {
    pdfAnalysis(input: $input) {
      answers {
        value
        error
        confidence
        pageNumber
      }
      rawResponse
    }
  }
`;

export const GET_PYXLSX2CSV2_CSV_DOWNLOAD_SIGNED_URL = gql`
  query getExtractPyXlsx2Csv2(
    $pipelineDefinitionId: String!
    $xlsxS3Bucket: String!
    $xlsxS3Key: String!
    $extractionParams: PyXlxs2CsvExtractionParams!
  ) {
    extractPyXlsx2Csv2(
      pipelineDefinitionId: $pipelineDefinitionId
      xlsxS3Bucket: $xlsxS3Bucket
      xlsxS3Key: $xlsxS3Key
      extractionParams: $extractionParams
    )
  }
`;

export const GET_PYXLSX2CSV2_UPLOAD_SIGNED_URL = gql`
  query getPyXslx2Csv2UploadSignedUrl($serializedParams: String!) {
    getSignedUploadUrl(
      feature: "pyXlsx2Csv2ExtractionQA"
      serializedParams: $serializedParams
    ) {
      url
      s3RequestFields
      queryResponseFields
    }
  }
`;

export const GET_PYXLSXVIRTUALSHEET2CSV_CSV_DOWNLOAD_SIGNED_URL = gql`
  query getExtractPyXlsxVirtualSheet2Csv(
    $pipelineDefinitionId: String!
    $xlsxS3Bucket: String!
    $xlsxS3Key: String!
    $extractionParams: PyXlxsVirtualSheet2CsvExtractionParams!
  ) {
    extractPyXlsxVirtualSheet2Csv(
      pipelineDefinitionId: $pipelineDefinitionId
      xlsxS3Bucket: $xlsxS3Bucket
      xlsxS3Key: $xlsxS3Key
      extractionParams: $extractionParams
    )
  }
`;

export const GET_AI2CSV_CSV_DOWNLOAD_SIGNED_URL = gql`
  query getExtractAi2Csv(
    $pipelineDefinitionId: String!
    $prompt: String!
    $documentsStr: String!
    $tool: Object!
    $baseFieldsTemplate: BaseFieldsTemplate
  ) {
    extractAi2Csv(
      pipelineDefinitionId: $pipelineDefinitionId
      prompt: $prompt
      documentsStr: $documentsStr
      tool: $tool
      baseFieldsTemplate: $baseFieldsTemplate
    )
  }
`;

export const GET_PYCSVVIRTUALSHEET_CSV_DOWNLOAD_SIGNED_URL = gql`
  query getExtractPyCSvVirtualSheet(
    $pipelineDefinitionId: String!
    $extractionParams: PyCsvVirtualSheetExtractionParams!
  ) {
    extractPyCsvVirtualSheet(
      pipelineDefinitionId: $pipelineDefinitionId
      extractionParams: $extractionParams
    )
  }
`;

export const GET_PYXLSXVIRTUALSHEET2CSV_UPLOAD_SIGNED_URL = gql`
  query getPyXslxVirtualSheet2CsvUploadSignedUrl($serializedParams: String!) {
    getSignedUploadUrl(
      feature: "pyXlsxVirtualSheet2CsvExtractionQA"
      serializedParams: $serializedParams
    ) {
      url
      s3RequestFields
      queryResponseFields
    }
  }
`;

export const GET_PYCVVIRTUALSHEET_UPLOAD_SIGNED_URL = gql`
  query getPyCsvVirtualSheetUploadSignedUrl($serializedParams: String!) {
    getSignedUploadUrl(
      feature: "pyCsvVirtualSheetExtractionQA"
      serializedParams: $serializedParams
    ) {
      url
      s3RequestFields
      queryResponseFields
    }
  }
`;

export const GET_AI2CSV_UPLOAD_SIGNED_URL = gql`
  query getAi2CsvSignedUrl($serializedParams: String!) {
    getSignedUploadUrl(
      feature: "ai2CsvQa"
      serializedParams: $serializedParams
    ) {
      url
      s3RequestFields
      queryResponseFields
    }
  }
`;

export const GET_ASSET_ANALYSIS = gql`
  query getAssetAnalysis($params: String!, $pipelineDefinitionId: UUID!) {
    result: extractAsset2Json(
      params: $params
      pipelineDefinitionId: $pipelineDefinitionId
    )
  }
`;

export const GET_TAPE_BUILDER_DOCUMENT_INFO = gql`
  query getBorrowingBasePipelineDocumentInfo(
    $artifactDocumentId: UUID!
    $dbView: Boolean!
  ) {
    borrowingBasePipelineArtifactDocumentInfo(
      artifactDocumentId: $artifactDocumentId
      dbView: $dbView
    ) {
      artifactDocumentId
      artifactsDocumentString
    }
  }
`;

export const GET_PIPELINE_INSTANCES = gql`
  query GetPipelineInstances($pipelineRunId: UUID!) {
    getArtifactInstancesForPipelineRun(pipelineRunId: $pipelineRunId) {
      artifactDocumentId
      artifactInstances {
        status
        id
        artifactId
        instanceRecordString
        updated_at
        downloads {
          url
          filename
        }
        jsonRecords
      }
    }
  }
`;

export const GET_BORROWING_BASE_PIPELINE_ARTIFACT_SLUGS_FROM_ARTIFACT_DOCUMENT = gql`
  query GetBorrowingBasePipelineArtifactSlugsFromArtifactDocument(
    $artifactDocumentId: UUID
  ) {
    artifactSlugs(artifactDocumentId: $artifactDocumentId)
  }
`;

export const GET_BORROWER_PIPELINES = gql`
  query GetPipelineRuns(
    $pipelineDefinitionId: UUID!
    $startDate: FullDateStr
    $endDate: FullDateStr
  ) {
    getPipelineRuns(
      pipelineDefinitionId: $pipelineDefinitionId
      startDate: $startDate
      endDate: $endDate
    ) {
      reportingDate
      fileVersion
      artifactDocumentId
      pipelineRunId
    }
  }
`;

export const SAVE_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENT_WIP = gql`
  mutation saveBorrowingBasePipelineArtifactDocumentWip(
    $pipelineDefinitionId: UUID!
    $artifactsDocumentString: String!
  ) {
    saveBorrowingBasePipelineArtifactDocumentWip(
      pipelineDefinitionId: $pipelineDefinitionId
      artifactsDocumentString: $artifactsDocumentString
    ) {
      response
    }
  }
`;

export const DELETE_BORROWING_BASE_PIPELINE_ARTIFACT_DOCUMENT_WIP = gql`
  mutation deleteBorrowingBasePipelineArtifactDocumentWip(
    $pipelineDefinitionId: UUID!
    $artifactDocumentId: UUID!
  ) {
    deleteBorrowingBasePipelineArtifactDocumentWip(
      pipelineDefinitionId: $pipelineDefinitionId
      artifactDocumentId: $artifactDocumentId
    ) {
      response
    }
  }
`;

export const GET_TAPEBUILDER_MONITOR_LATEST_PIPELINE = gql`
  query GetTapeBuilderLatestPipelineRun($pipelineDefinitionId: UUID!) {
    getPipelineRuns(pipelineDefinitionId: $pipelineDefinitionId, limit: 1) {
      reportingDate
      fileVersion
      artifactDocumentId
      pipelineRunId
    }
  }
`;

export const GET_TAPEBUILDER_PIPELINE_RUN = gql`
  query GetTapeBuilderPipelineRun($pipelineRunId: UUID!) {
    getPipelineRunById(pipelineRunId: $pipelineRunId) {
      reportingDate
      fileVersion
      artifactDocumentId
    }
  }
`;

export const GET_TAPE_BUILDER_NODE_POSITIONS = gql`
  query GetTapeBuilderNodePositions(
    $artifactDocumentId: UUID!
    $purpose: NodePositionPurpose!
  ) {
    nodePositions: getNodePositions(
      artifactDocumentId: $artifactDocumentId
      purpose: $purpose
    ) {
      artifactIdentifier
      x
      y
    }
  }
`;

export const GET_GRAPH_WITH_POSITIONS = gql`
  query GetGraphWithPositions(
    $artifactDocumentId: UUID!
    $dbView: Boolean!
    $purpose: NodePositionPurpose!
  ) {
    borrowingBasePipelineArtifactDocumentInfo(
      artifactDocumentId: $artifactDocumentId
      dbView: $dbView
    ) {
      artifactDocumentId
      artifactsDocumentString
    }
    nodePositions: getNodePositions(
      artifactDocumentId: $artifactDocumentId
      purpose: $purpose
    ) {
      artifactIdentifier
      x
      y
    }
  }
`;

export const STORE_TAPE_BUILDER_NODE_POSITIONS = gql`
  mutation setNodePositions(
    $artifactDocumentId: UUID!
    $purpose: NodePositionPurpose!
    $positions: [NodePositionInput!]!
  ) {
    setNodePositions(
      artifactDocumentId: $artifactDocumentId
      purpose: $purpose
      positions: $positions
    )
  }
`;
